if (!customElements.get('dayla-wholesale-builder')) {
  customElements.define(
    'dayla-wholesale-builder',
    class DaylaWholesaleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.config = window.DaylaWholesaleConfig;
        this.product = JSON.parse(this.querySelector('[data-dayla-product-json]').textContent);
        this.minimum = Number(this.dataset.minimum) || this.config.defaultMinimumQuantity;
        this.querySelector('[data-dayla-minimum-label]').textContent = this.minimum;
        this.colorIndex = this.findOptionIndex(this.dataset.colorOption, this.config.colorOptionNames);
        this.sizeIndex = this.findOptionIndex(this.dataset.sizeOption, this.config.sizeOptionNames);
        this.groups = this.buildGroups();
        this.state = new Map();
        this.saved = new Set();
        this.colorSelect = this.querySelector('[data-dayla-color-select]');
        this.colorTrigger = this.querySelector('[data-dayla-color-trigger]');
        this.colorTriggerContent = this.querySelector('[data-dayla-color-trigger-content]');
        this.colorList = this.querySelector('[data-dayla-color-list]');
        this.activeBuilder = this.querySelector('[data-dayla-active-builder]');
        this.selectedPanel = this.querySelector('[data-dayla-selected-colors]');
        this.selectedList = this.querySelector('[data-dayla-selected-list]');
        this.submitButton = this.querySelector('[data-dayla-submit]');
        this.error = this.querySelector('[data-dayla-builder-error]');
        this.renderQueued = false;
        this.pendingRenderActive = false;
        this.pendingRenderSummary = false;
        this.renderColorOptions();
        this.bindEvents();
        this.selectColor(this.groups.keys().next().value);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
      }

      findOptionIndex(explicitName, fallbackNames) {
        const names = explicitName ? [explicitName.toLowerCase()] : fallbackNames;
        return this.product.options.findIndex((name) => names.includes(name.toLowerCase()));
      }

      buildGroups() {
        const groups = new Map();
        this.product.variants.forEach((variant) => {
          const color = this.colorIndex >= 0 ? variant.options[this.colorIndex] : 'Default';
          const size = this.sizeIndex >= 0 ? variant.options[this.sizeIndex] : variant.title || 'One Size';
          if (!groups.has(color)) groups.set(color, []);
          groups.get(color).push({
            id: variant.id,
            color,
            size,
            price: variant.price,
            sku: variant.sku,
            available: variant.available,
            featuredMediaId: variant.featured_media?.id || null,
            image: this.getImageUrl(variant.featured_image) || this.getImageUrl(variant.featured_media?.preview_image),
          });
        });
        return groups;
      }

      getQuantities(color) {
        if (!this.state.has(color)) this.state.set(color, new Map());
        return this.state.get(color);
      }

      getTotal(color) {
        return [...this.getQuantities(color).values()].reduce((sum, quantity) => sum + quantity, 0);
      }

      renderColorOptions() {
        const selectedColor = this.activeColor || this.groups.keys().next().value;
        this.colorList.innerHTML = [...this.groups.keys()]
          .map((color) => {
            const total = this.getTotal(color);
            const isSaved = this.saved.has(color);
            const status = isSaved ? `${total} selected` : total ? `${total} draft` : 'Not selected';
            return `
              <button
                class="dayla-wholesale-builder__color-option${isSaved ? ' is-configured' : ''}"
                type="button"
                role="option"
                aria-selected="${color === selectedColor}"
                tabindex="-1"
                data-dayla-color-option
                data-color="${this.escapeAttribute(color)}"
              >
                ${this.renderThumbnail(this.getGroupImage(color))}
                <span class="dayla-wholesale-builder__color-copy">
                  <strong>${this.escape(color)}</strong>
                  <span>${this.escape(status)}</span>
                </span>
              </button>`;
          })
          .join('');

        if (!selectedColor || !this.groups.has(selectedColor)) return;
        const total = this.getTotal(selectedColor);
        const status = this.saved.has(selectedColor)
          ? `${total} selected`
          : total
            ? `${total} draft`
            : 'Not selected';
        this.colorTriggerContent.innerHTML = `
          ${this.renderThumbnail(this.getGroupImage(selectedColor))}
          <span class="dayla-wholesale-builder__color-copy">
            <strong>${this.escape(selectedColor)}</strong>
            <span>${this.escape(status)}</span>
          </span>`;
      }

      bindEvents() {
        this.colorTrigger.addEventListener('click', () => this.toggleColorSelector());
        this.colorTrigger.addEventListener('keydown', (event) => {
          if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          this.openColorSelector(event.key === 'ArrowUp' ? 'last' : 'active');
        });
        this.colorList.addEventListener('click', (event) => {
          const option = event.target.closest('[data-dayla-color-option]');
          if (!option) return;
          this.selectColor(option.dataset.color);
          this.closeColorSelector(true);
        });
        this.colorList.addEventListener('keydown', (event) => this.handleColorListKeydown(event));
        this.colorSelect.addEventListener('focusout', () => {
          window.setTimeout(() => {
            if (!this.colorSelect.contains(document.activeElement)) this.closeColorSelector();
          });
        });
        this.handleDocumentClick = (event) => {
          if (!this.colorSelect.contains(event.target)) this.closeColorSelector();
        };
        document.addEventListener('click', this.handleDocumentClick);
        this.activeBuilder.addEventListener('click', (event) => {
          try {
            const button = event.target.closest('[data-dayla-quantity-action]');
            if (button) {
              event.preventDefault();
              this.changeQuantity(button.dataset.variantId, Number(button.dataset.delta));
            }

            if (event.target.closest('[data-dayla-save-color]')) this.saveActiveColor();
          } catch (error) {
            this.handleRenderError(error);
          }
        });
        this.selectedList.addEventListener('click', (event) => {
          try {
            const edit = event.target.closest('[data-dayla-edit-color]');
            const remove = event.target.closest('[data-dayla-remove-color]');
            if (edit) this.selectColor(edit.dataset.color);
            if (remove) this.removeColor(remove.dataset.color);
          } catch (error) {
            this.handleRenderError(error);
          }
        });
        this.submitButton.addEventListener('click', () => this.submit());
      }

      toggleColorSelector() {
        if (this.colorTrigger.getAttribute('aria-expanded') === 'true') this.closeColorSelector();
        else this.openColorSelector('active');
      }

      openColorSelector(focusTarget = 'active') {
        this.colorList.hidden = false;
        this.colorTrigger.setAttribute('aria-expanded', 'true');
        const options = [...this.colorList.querySelectorAll('[data-dayla-color-option]')];
        const target =
          focusTarget === 'last'
            ? options.at(-1)
            : options.find((option) => option.dataset.color === this.activeColor) || options[0];
        target?.focus();
      }

      closeColorSelector(returnFocus = false) {
        if (this.colorList.hidden) return;
        this.colorList.hidden = true;
        this.colorTrigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.colorTrigger.focus();
      }

      handleColorListKeydown(event) {
        const options = [...this.colorList.querySelectorAll('[data-dayla-color-option]')];
        const currentIndex = options.indexOf(document.activeElement);
        let nextIndex = currentIndex;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeColorSelector(true);
          return;
        }
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = options.length - 1;
        else if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, options.length - 1);
        else if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
        else return;

        event.preventDefault();
        options[nextIndex]?.focus();
      }

      selectColor(color) {
        if (!this.groups.has(color)) return;
        this.activeColor = color;
        this.renderBuilderState({ summary: false });
        this.updateGalleryForColor(color);
      }

      updateGalleryForColor(color) {
        const mediaId = this.getRepresentativeMediaId(color);
        if (!mediaId) return;

        const activateMedia = () => {
          const gallery = document.querySelector(`#MediaGallery-${this.dataset.sectionId}`);
          gallery?.setActiveMedia?.(`${this.dataset.sectionId}-${mediaId}`, false);
        };

        if (customElements.get('media-gallery')) activateMedia();
        else customElements.whenDefined('media-gallery').then(activateMedia);
      }

      getRepresentativeMediaId(color) {
        const variants = this.groups.get(color) || [];
        const quantities = this.state.get(color);
        return (
          variants.find((variant) => variant.available && variant.featuredMediaId)?.featuredMediaId ||
          variants.find(
            (variant) => (quantities?.get(variant.id) || 0) > 0 && variant.featuredMediaId
          )?.featuredMediaId ||
          variants.find((variant) => variant.featuredMediaId)?.featuredMediaId ||
          this.product.featured_media?.id ||
          null
        );
      }

      changeQuantity(variantId, delta) {
        const variants = this.groups.get(this.activeColor);
        const variant = variants.find((item) => String(item.id) === String(variantId));
        if (!variant?.available) return;
        const quantities = this.getQuantities(this.activeColor);
        const current = quantities.get(variant.id) || 0;
        let next;
        if (variants.length === 1) {
          if (delta > 0) next = current === 0 ? this.minimum : current + 1;
          else next = current <= this.minimum ? 0 : current - 1;
        } else {
          next = Math.max(0, current + delta);
        }
        quantities.set(variant.id, next);
        this.requestRender();
      }

      saveActiveColor() {
        const total = this.getTotal(this.activeColor);
        if (total < this.minimum) return;
        this.saved.add(this.activeColor);
        this.requestRender();
      }

      removeColor(color) {
        this.state.delete(color);
        this.saved.delete(color);
        this.requestRender({ active: this.activeColor === color, summary: true });
      }

      requestRender({ active = true, summary = true } = {}) {
        this.pendingRenderActive = this.pendingRenderActive || active;
        this.pendingRenderSummary = this.pendingRenderSummary || summary;
        if (this.renderQueued) return;

        this.renderQueued = true;
        window.requestAnimationFrame(() => {
          const renderActive = this.pendingRenderActive;
          const renderSummary = this.pendingRenderSummary;
          this.renderQueued = false;
          this.pendingRenderActive = false;
          this.pendingRenderSummary = false;
          this.renderBuilderState({ active: renderActive, summary: renderSummary });
        });
      }

      renderBuilderState({ active = true, summary = true } = {}) {
        try {
          if (!this.activeColor || !this.groups.has(this.activeColor)) {
            this.activeColor = this.groups.keys().next().value;
          }
          if (active) this.renderActiveColor();
          if (summary) this.renderSummary();
          else this.renderColorOptions();
        } catch (error) {
          this.handleRenderError(error);
        }
      }

      handleRenderError(error) {
        console.error('[DAYLA WHOLESALE BUILDER]', error);
        this.recoverState();

        try {
          this.renderActiveColor();
          this.renderSummary();
        } catch (recoveryError) {
          console.error('[DAYLA WHOLESALE BUILDER RECOVERY]', recoveryError);
          this.showError('Wholesale order builder needs a refresh. Please reload this page and try again.');
        }
      }

      recoverState() {
        const validColors = new Set(this.groups.keys());
        [...this.state.keys()].forEach((color) => {
          if (!validColors.has(color)) {
            this.state.delete(color);
            return;
          }

          const validVariantIds = new Set(this.groups.get(color).map((variant) => variant.id));
          const quantities = this.getQuantities(color);
          [...quantities.keys()].forEach((variantId) => {
            const quantity = quantities.get(variantId);
            if (!validVariantIds.has(variantId) || !Number.isFinite(quantity) || quantity < 0) {
              quantities.delete(variantId);
            }
          });
        });

        [...this.saved].forEach((color) => {
          if (!validColors.has(color)) this.saved.delete(color);
        });

        if (!this.activeColor || !validColors.has(this.activeColor)) {
          this.activeColor = this.groups.keys().next().value;
        }
      }

      renderActiveColor() {
        const variants = this.groups.get(this.activeColor);
        const quantities = this.getQuantities(this.activeColor);
        const total = this.getTotal(this.activeColor);
        const remaining = Math.max(0, this.minimum - total);
        const availableVariantCount = variants.filter((variant) => variant.available).length;
        const isSingle = availableVariantCount === 1;
        const status =
          total === 0 ? 'Not selected' : total >= this.minimum ? 'MOQ met' : `Add ${remaining} more to meet MOQ`;
        const buttonText =
          total === 0
            ? 'NO UNITS SELECTED'
            : total < this.minimum
              ? `ADD ${remaining} MORE TO MEET MOQ`
              : this.saved.has(this.activeColor)
                ? 'COLOR SAVED'
                : 'SAVE COLOR TO ORDER';

        this.activeBuilder.innerHTML = `
          <div class="dayla-wholesale-builder__color-head">
            <div><span>Configuring</span><h2>${this.escape(this.activeColor)}</h2></div>
            <strong class="${total >= this.minimum ? 'is-valid' : ''}">${total} / ${this.minimum}</strong>
          </div>
          <p class="dayla-wholesale-builder__status">${status}</p>
          <div class="dayla-wholesale-builder__sizes">
            ${variants
              .map((variant) => {
                const quantity = quantities.get(variant.id) || 0;
                return `
                  <div class="dayla-wholesale-builder__size${variant.available ? '' : ' is-unavailable'}">
                    <div>
                      <strong>${this.escape(variant.size)}</strong>
                      <span>${variant.available ? `${this.money(variant.price)} each${variant.sku ? ` · SKU ${this.escape(variant.sku)}` : ''}` : 'Unavailable'}</span>
                    </div>
                    <div class="dayla-wholesale-builder__stepper">
                      <button type="button" data-dayla-quantity-action data-variant-id="${variant.id}" data-delta="-1" aria-label="Decrease ${this.escape(variant.size)}">−</button>
                      <output>${quantity}</output>
                      <button type="button" data-dayla-quantity-action data-variant-id="${variant.id}" data-delta="1" aria-label="Increase ${this.escape(variant.size)}" ${variant.available ? '' : 'disabled'}>+</button>
                    </div>
                  </div>`;
              })
              .join('')}
          </div>
          ${isSingle ? `<p class="dayla-wholesale-builder__hint">Start with ${this.minimum} units per color. Add more in single-unit increments.</p>` : ''}
          <button class="dayla-wholesale-builder__save" type="button" data-dayla-save-color ${total < this.minimum ? 'disabled' : ''}>${buttonText}</button>
        `;
      }

      renderSummary() {
        const colors = [...this.saved].filter((color) => this.getTotal(color) >= this.minimum);
        this.selectedPanel.hidden = colors.length === 0;
        this.selectedList.innerHTML = colors
          .map((color) => {
            const quantities = this.getQuantities(color);
            const lines = this.groups
              .get(color)
              .filter((variant) => (quantities.get(variant.id) || 0) > 0)
              .map((variant) => `<li>${this.escape(variant.size)} ${quantities.get(variant.id)}</li>`)
              .join('');
            const image = this.getSummaryImage(color, quantities);
            return `
              <article class="dayla-wholesale-builder__summary">
                ${this.renderThumbnail(image, 'dayla-wholesale-builder__summary-image')}
                <div class="dayla-wholesale-builder__summary-content">
                  <div class="dayla-wholesale-builder__summary-head">
                    <strong>${this.escape(color)} · ${this.getTotal(color)} units</strong>
                    <span>MOQ met</span>
                  </div>
                  <ul>${lines}</ul>
                  <div class="dayla-wholesale-builder__summary-actions">
                    <button type="button" data-dayla-edit-color data-color="${this.escapeAttribute(color)}">Edit</button>
                    <button type="button" data-dayla-remove-color data-color="${this.escapeAttribute(color)}">Remove</button>
                  </div>
                </div>
              </article>`;
          })
          .join('');

        let units = 0;
        let subtotal = 0;
        colors.forEach((color) => {
          const quantities = this.getQuantities(color);
          this.groups.get(color).forEach((variant) => {
            const quantity = quantities.get(variant.id) || 0;
            units += quantity;
            subtotal += quantity * variant.price;
          });
        });
        this.querySelector('[data-dayla-total-units]').textContent = units;
        this.querySelector('[data-dayla-subtotal]').textContent = this.money(subtotal);
        const hasInvalidDraft = [...this.state.keys()].some((color) => {
          const total = this.getTotal(color);
          return total > 0 && total < this.minimum;
        });
        this.submitButton.disabled = colors.length === 0 || hasInvalidDraft;
        this.renderColorOptions();
      }

      getImageUrl(image) {
        if (!image) return '';
        if (typeof image === 'string') return image;
        return image.src || image.url || '';
      }

      getProductImage() {
        return (
          this.getImageUrl(this.product.featured_image) ||
          this.getImageUrl(this.product.featured_media?.preview_image) ||
          ''
        );
      }

      getGroupImage(color) {
        const variants = this.groups.get(color) || [];
        return (
          variants.find((variant) => variant.available && variant.image)?.image ||
          variants.find((variant) => variant.image)?.image ||
          this.getProductImage()
        );
      }

      getSummaryImage(color, quantities) {
        const variants = this.groups.get(color) || [];
        return (
          variants.find((variant) => (quantities.get(variant.id) || 0) > 0 && variant.image)?.image ||
          variants.find((variant) => variant.image)?.image ||
          this.getProductImage()
        );
      }

      renderThumbnail(image, className = 'dayla-wholesale-builder__color-image') {
        if (!image) return `<span class="${className} is-placeholder" aria-hidden="true"></span>`;
        return `<img class="${className}" src="${this.escapeAttribute(image)}" alt="" width="96" height="96" loading="lazy">`;
      }

      async submit() {
        const items = [];
        [...this.saved].forEach((color) => {
          const quantities = this.getQuantities(color);
          const variants = this.groups.get(color);
          variants.forEach((variant) => {
            const quantity = quantities.get(variant.id) || 0;
            if (!quantity) return;
            items.push({
              id: variant.id,
              quantity,
              properties: {
                _dayla_color: color,
                _dayla_size: variant.size,
                _dayla_group: `${this.dataset.productId}::${color}`,
                _dayla_group_variant_count: String(variants.filter((item) => item.available).length || 1),
                _dayla_moq: String(this.minimum),
              },
            });
          });
        });
        if (!items.length) return;

        this.setLoading(true);
        this.showError('');
        try {
          const cart = document.querySelector('cart-drawer');
          const body = {
            items,
            sections: cart ? cart.getSectionsToRender().map((section) => section.id) : [],
            sections_url: window.location.pathname,
          };
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add order.');
          if (cart) {
            cart.setActiveElement(this.submitButton);
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            cart.renderContents(result);
          } else {
            window.location.href = window.routes.cart_url;
          }
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        this.submitButton.disabled = loading;
        this.submitButton.classList.toggle('is-loading', loading);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      showError(message) {
        this.error.hidden = !message;
        this.error.textContent = message;
      }

      money(cents) {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
      }

      escape(value) {
        const element = document.createElement('div');
        element.textContent = value ?? '';
        return element.innerHTML;
      }

      escapeAttribute(value) {
        return this.escape(value).replaceAll('"', '&quot;').replaceAll("'", '&#39;');
      }
    }
  );
}
